import * as React from "react"
import { Separator } from "@workspace/ui/components/shadcn/separator"
import { Button } from "@workspace/ui/components/shadcn/button"
import { Rows } from "./Rows"
import { Field } from "./Field"
import type { FormSpec } from "./formSpec"
import { buildLayout } from "./formLayout"
import { getFrameUiConfig } from "./formUi"
import { useFormState } from "./useFormState"
import { useValidation } from "./useValidation"
import { useFormApi } from "./useFormApi"
import { useSubmit } from "./useSubmit"

type Props = {
    spec: FormSpec
    onSuccess?: (result: unknown, values: Record<string, unknown>) => void
    onError?: (error: unknown) => void
}

export default function FormFrame({ spec, onSuccess, onError }: Props) {
    // Refs für Option 2 (Stack → Textarea)
    const stackRefs = React.useRef<Record<number, HTMLDivElement | null>>({})
    const textareaRefs = React.useRef<Record<number, HTMLTextAreaElement | null>>({})

    const ui = React.useMemo(() => getFrameUiConfig(spec), [spec])
    const layout = React.useMemo(() => buildLayout(spec), [spec])

    const {
        values,
        setValues,
        errors,
        setErrors,
        errorGlobal,
        setErrorGlobal,
        loading,
        setLoading,
        formKey,
        didSubmitRef,
        hardReset,
    } = useFormState(spec)

    const {
        shouldValidate,
        validateField,
        validateFieldWithValues,
        validateAll,
        validateAllWithValues,
    } = useValidation({
        spec,
        values,
        setErrors,
        didSubmitRef,
    })

    const form = useFormApi({
        spec,
        values,
        setValues,
        errors,
        setErrors,
        shouldValidate,
        validateField,
        validateFieldWithValues,
    })

    const { handleSubmit } = useSubmit({
        spec,
        values,
        form,
        setLoading,
        setErrorGlobal,
        validateAll,
        didSubmitRef,
        onSuccess,
        onError,
    })

    // Initial Reset + optional validate-on-mount
    React.useEffect(() => {
        const nextValues = hardReset({ withNewKey: true })
        if (spec.form?.validateOnMount === true) {
            void validateAllWithValues(nextValues)
        }
    }, [hardReset, spec, validateAllWithValues])

    // OPTION 2: Höhe einmalig vom Stack auf Textarea übertragen
    React.useEffect(() => {
        Object.keys(stackRefs.current).forEach((key) => {
            const rowIndex = Number(key)
            const stackEl = stackRefs.current[rowIndex]
            const textareaEl = textareaRefs.current[rowIndex]

            if (!stackEl || !textareaEl) return

            const h = stackEl.offsetHeight
            textareaEl.style.height = `${h}px`
        })
    }, [])

    const canSubmit = React.useMemo(() => {
        if (loading) return false
        return Object.keys(errors).length === 0
    }, [loading, errors])

    const inner = (
        <form
            key={formKey}
            noValidate
            autoComplete={spec.form?.autoComplete ?? "on"}
            className="space-y-6"
            onSubmit={handleSubmit}
        >
            {ui.headerTitle || ui.headerSubtitle ? (
                <div className={`grid gap-1 ${ui.headerAlign}`}>
                    {ui.headerTitle ? <h1 className="text-xl font-semibold">{ui.headerTitle}</h1> : null}
                    {ui.headerSubtitle ? (
                        <p className="text-sm text-muted-foreground">{ui.headerSubtitle}</p>
                    ) : null}
                </div>
            ) : null}

            <Rows
                layout={layout}
                renderCol={(fieldKey, col, rowIndex) => {
                    const def = (spec.fields as any)?.[fieldKey]
                    if (!def) return null

                    // Textarea → Ref merken
                    if (def.type === "textarea") {
                        return (
                            <Field
                                def={def}
                                form={form}
                                textareaRef={(el: HTMLTextAreaElement | null) => {
                                    textareaRefs.current[rowIndex] = el
                                }}
                            />
                        )
                    }

                    // Stack-Referenz: erstes Feld im Stack reicht
                    if (col.stack) {
                        return (
                            <div
                                ref={(el) => {
                                    stackRefs.current[rowIndex] = el
                                }}
                            >
                                <Field def={def} form={form} />
                            </div>
                        )
                    }

                    return <Field def={def} form={form} />
                }}
            />

            {errorGlobal ? <div className="text-sm text-red-500">{errorGlobal}</div> : null}
            {ui.showDividerBeforeSubmit ? <Separator className="my-4" /> : null}

            <Button type="submit" disabled={!canSubmit} className={ui.submitBtnClass}>
                {loading ? ui.submitLoadingLabel : ui.submitLabel}
            </Button>

            {spec.ui?.footer ? (
                <div className="text-center text-sm text-muted-foreground mt-6">
                    {spec.ui.footer.prefix ? <span>{spec.ui.footer.prefix} </span> : null}
                    {spec.ui.footer.link ? (
                        <a className="underline underline-offset-4" href={spec.ui.footer.link.href}>
                            {spec.ui.footer.link.text}
                        </a>
                    ) : null}
                    {spec.ui.footer.suffix ? <span> {spec.ui.footer.suffix}</span> : null}
                </div>
            ) : null}
        </form>
    )

    if (!ui.frameOn) return inner
    return <div className={ui.frameClass}>{inner}</div>
}
